import { fail } from "src/utils";
import {
  ReviewProjectImpact_CostCodeFragment,
  ReviewProjectImpact_ProjectCostCodeFragment,
  ReviewProjectImpact_ScheduleImpactFragment,
  ReviewProjectScheduleImpactQueryVariables,
} from "./ReviewImpactStep.generated";

/** What a change event line item does to a cost code: the line items are already deltas, cost and price alike. */
export type ReviewImpactNewBudgetRow = {
  costCode: ReviewProjectImpact_CostCodeFragment;
  totalCostInCents: number;
  totalPriceInCents: number;
};

export type ProjectBudgetRow = {
  costCode: ReviewProjectImpact_CostCodeFragment;
  currentBudgetInCents: number;
  budgetChangeInCents: number;
  newPriceInCents: number;
};

/**
 * Given the current project cost codes and the change event's line items, create the rows for the budget detail
 * table: one per cost code the project budgets for or the change event touches, with the line items' deltas summed.
 */
export function createProjectBudgetRows(
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[],
  newBudgetRows: ReviewImpactNewBudgetRow[],
): ProjectBudgetRow[] {
  const pccByCostCodeId = projectCostCodes.keyBy((pcc) => pcc.costCode.id);
  const newBudgetRowsByCostCodeId = newBudgetRows.groupBy(({ costCode }) => costCode.id);

  return [...projectCostCodes.map((pcc) => pcc.costCode), ...newBudgetRows.map(({ costCode }) => costCode)]
    .uniqueByKey("id")
    .map((costCode) => {
      const rows = newBudgetRowsByCostCodeId[costCode.id] ?? [];
      return {
        costCode,
        currentBudgetInCents: pccByCostCodeId[costCode.id]?.totalCostInCents ?? 0,
        budgetChangeInCents: rows.sum((row) => row.totalCostInCents),
        newPriceInCents: rows.sum((row) => row.totalPriceInCents),
      };
    });
}

export type ReviewImpactBudgetTotals = {
  currentBudgetInCents: number;
  newBudgetInCents: number;
  priceImpactInCents: number;
};

/**
 * The project's budget before and after the change, and what the change does to its price. Each row is already a
 * delta, so the new budget is today's total plus every row's change, and the price impact is every row's price change.
 */
export function reviewImpactBudgetTotals(
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[],
  rows: ProjectBudgetRow[],
): ReviewImpactBudgetTotals {
  const currentBudgetInCents = projectCostCodes.sum((pcc) => pcc.totalCostInCents);
  return {
    currentBudgetInCents,
    newBudgetInCents: currentBudgetInCents + rows.sum((row) => row.budgetChangeInCents),
    priceImpactInCents: rows.sum((row) => row.newPriceInCents),
  };
}

/**
 * The schedule impact query takes exactly one of a change event or a checkout config, and resolves the main house/ADU
 * side of the config off the project itself. A dry-run change event only exists in Apollo's cache, so without a saved
 * draft the config is projected instead.
 */
export function scheduleImpactVariablesFor(
  projectId: string,
  savedChangeEventId: string | null | undefined,
  checkoutConfigId: string | null | undefined,
): ReviewProjectScheduleImpactQueryVariables {
  return savedChangeEventId
    ? { projectId, changeEventId: savedChangeEventId }
    : { projectId, checkoutConfigId: checkoutConfigId ?? fail("No checkout config id") };
}

/**
 * How far the configuration moves the completion date, in months since days are hard to picture at that scale, or in
 * days when it moves less than a month, because "+0 months" says nothing. Undefined when there is no shift to measure.
 */
export function scheduleShift(
  scheduleImpact: Pick<
    ReviewProjectImpact_ScheduleImpactFragment,
    "currentCompletionDate" | "proposedCompletionDate" | "differenceInDays"
  >,
): { count: number; unit: "day" | "month" } | undefined {
  const { currentCompletionDate, proposedCompletionDate, differenceInDays } = scheduleImpact;
  if (!currentCompletionDate || !proposedCompletionDate || !differenceInDays) return undefined;
  const { months } = currentCompletionDate.until(proposedCompletionDate, { largestUnit: "month" });
  return months === 0 ? { unit: "day", count: differenceInDays } : { unit: "month", count: months };
}
